import React from 'react';
import isEqual from 'lodash/isEqual';
import { ff } from 'helpers/featureFlags';
import { stateFormErrorConnector } from 'components/Form/shared/stateFormErrors';
import classnames from 'classnames';
import ValidationElement from '../ValidationElement';
import DateControl from '../DateControl';
import Checkbox from '../Checkbox';
import Show from '../Show';
import { now, validDate } from '../../Section/History/dateranges';
import DateRangeValidator from '../../../validators/daterange';
import { setAttribute } from '../shared/setAttribute';
import SvgComponent from '../SvgComponent';

class DateRange extends ValidationElement {
  constructor(props) {
    super(props);

    const touched = this.isTouched(props.to.year, props.to.month, props.to.day)
      || this.isTouched(props.from.year, props.from.month, props.from.day);
    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
      from: { ...props.from, touched },
      to: { ...props.to, touched },
      present: props.present,
      title: props.title || 'Date Range',
      error: false,
    };

    this.storeErrors = this.storeErrors.bind(this);
    this.update = this.update.bind(this);
    this.updateFrom = this.updateFrom.bind(this);
    this.updateTo = this.updateTo.bind(this);
    this.updatePresent = this.updatePresent.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleErrorFrom = this.handleErrorFrom.bind(this);
    this.handleErrorTo = this.handleErrorTo.bind(this);
    this.handleErrorPresent = this.handleErrorPresent.bind(this);
    this.handleDisable = this.handleDisable.bind(this);
    this.errors = [];

    this.to = React.createRef();
    this.from = React.createRef();
  }

  isTouched(year, month, day) {
    return year !== '' && month !== '' && day !== '' && parseInt(month, 10) >= 1 && parseInt(day, 10) >= 1 && parseInt(year, 10) >= 1;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.disabled !== this.props.disabled) {
      // Check when disable flag is updated
      this.handleDisable(nextProps);
    }

    if (
      !isEqual(nextProps.from, this.props.from)
      || !isEqual(nextProps.to, this.props.to)
      || nextProps.present !== this.props.present
    ) {
      if (!isEqual(nextProps.from, this.state.from)) {
        this.updateFrom(nextProps.from);
      }

      if (nextProps.to?.month !== this.state.to?.month || nextProps.to?.year !== this.state.to?.year) {
        this.updateTo(nextProps.to);
      }
    }
  }

  handleDisable(nextProps) {
    let errors = [...this.errors] || [];
    if (nextProps.disabled) {
      errors = errors.map((err) => ({
        code: err.code,
        valid: null,
        uid: err.uid,
      }));
    }
    this.props.onError('', errors);
    this.setState();
  }

  update(queue) {
    // If we have an update, clear existing error and let handleError do its job
    this.setState({ error: false });
    this.props.onUpdate({
      name: this.props.name,
      from: { ...this.state.from, touched: true },
      to: { ...this.state.to, touched: true },
      present: this.state.present,
      ...queue,
    });
  }

  updateFrom(values) {
    this.setState({ from: values }, () => {
      this.update({ from: values });
    });
  }

  updateTo(values) {
    this.setState({ to: values }, () => {
      this.update({ to: values });
    });
  }

  updatePresent(values) {
    // Get a handle to current state values as well as set the value for the current
    // element that triggered a change
    const futureState = {
      ...this.state,
      present: !this.state.present,
    };

    // If present is true then make the "to" date equal to today
    if (!this.state.present && futureState.present) {
      futureState.to = {
        year: `${now.getFullYear()}`,
        month: `${now.getMonth() + 1}`,
        day: `${now.getDate()}`,
        estimated: false,
      };
    } else if (this.state.present && !futureState.present) {
      futureState.to = {
        year: '',
        month: '',
        day: '',
        estimated: false,
      };
    }
    const errors = this.errors.map((err) => {
      if (err.code.indexOf('daterange.to') === -1) {
        return err;
      }

      return {
        code: err.code,
        valid: null,
        uid: err.uid,
      };
    });
    this.errors = errors;

    this.setState(futureState, () => {
      this.update(futureState);
    });
  }

  storeErrors(arr = [], callback) {
    const errors = [...this.errors];
    for (const e of arr) {
      const idx = errors.findIndex((x) => x.uid === e.uid && x.code === e.code);
      if (idx !== -1) {
        errors[idx] = { ...e };
      } else {
        errors.push({ ...e });
      }
    }
    this.errors = [...errors];
    callback();
  }

  handleErrorFrom(value, arr) {
    return this.handleError('from', value, arr);
  }

  handleErrorTo(value, arr) {
    return this.handleError('to', value, arr);
  }

  handleErrorPresent(value, arr) {
    return this.handleError('present', value, arr);
  }

  handleError(code, value, arr) {
    let arr2 = arr.map((err) => ({
      code: `${this.props.dateRangePrefix ? this.props.dateRangePrefix : 'daterange'
      }.${code}.${err.code.replace('date.', '')}`,

      valid: err.valid,
      uid: err.uid,
    }));

    // Handle required
    arr2 = arr2.concat(
      this.constructor.errors
        .filter((err) => err.code === 'required')
        .map((err) => {
          const value2 = { ...this.state };
          return {
            code: `daterange.${err.code}`,
            valid: err.func(value2, this.props),
            uid: this.state.uid,
          };
        })
    );

    // Introducing local state to the DateControl so it can determine
    // if there were **any** errors found in other child components.
    this.storeErrors(arr2, () => {
      const existingErr = this.errors.some((e) => e.valid === false);
      let local = [];
      const noneRequiredErrors = this.constructor.errors.filter((err) => err.code !== 'required');

      if (!existingErr && this.state.from && this.state.to) {
        // Prepare some properties for the error testing
        const props = {
          from: this.state.from,
          to: this.state.to,
        };

        local = noneRequiredErrors.map((err) => ({
          code: err.code,
          valid: err.func(null, props),
          uid: this.state.uid,
        }));
      } else {
        local = noneRequiredErrors.map((err) => ({
          code: err.code,
          valid: null,
          uid: this.state.uid,
        }));
      }

      this.setState({ error: local.some((x) => x.valid === false) }, () => {
        this.props.onError(value, [...arr2].concat(local));
      });
    });

    return arr2;
  }

  render() {
    const { forceMinDate, usePvqToDate, usePvqFromDate } = this.props;
    const isFFActive = ff(this.props.features).getActive('EN-4733');
    const isPvq = usePvqToDate || usePvqFromDate;
    return isPvq ? (
      <div className={classnames('daterange', this.props.className)}>
        {usePvqFromDate && (
        <div className="from-grid">
          <div className="from-label">{this.props.fromDateLabel}</div>
          <DateControl
            attribute={setAttribute(this.props.attribute, 'from')}
            attributeList={[this.props.attribute]}
            name="from"
            className="from"
            purposeLabel={`${this.props.fromDateLabel} Date`}
            ref={this.from}
            {...this.state.from}
            onUpdate={this.updateFrom} // eslint-disable-line @typescript-eslint/unbound-method
            minDate={this.props.minDate}
            minDateEqualTo={this.props.minDateEqualTo}
            maxDate={this.props.maxDate}
            maxDateEqualTo={this.props.maxDateEqualTo}
            prefix={this.props.prefix}
            relationship={this.props.relationship}
            onError={this.handleErrorFrom} // eslint-disable-line @typescript-eslint/unbound-method
            disabled={this.props.disabled}
            required={this.props.required}
            hideMonth={this.props.hideFromMonth}
            hideDay={this.props.hideFromDay}
            forceMinDate={forceMinDate}
          />
        </div>
        )}
        <div className="arrow">
          <div className="arrow-right">
            <SvgComponent className="arrow-right" src="/img/arrow-right.svg" />
          </div>
        </div>
        {usePvqToDate && (
          <>
            <div className="to-grid">
              <div className="from-label">{this.props.toDateLabel}</div>
              <DateControl
                className={classnames('to', { 'usa-input-error': !isFFActive && this.state.error })}
                name="to"
                purposeLabel={`${this.props.toDateLabel} Date`}
                subsectionInfo={this.props.subsection?.subsectionKey}
                attribute={setAttribute(this.props.attribute, 'to')}
                attributeList={[this.props.attribute]}
                ref={this.to}
                {...this.state.to}
                disabled={this.state.present || this.props.disabled}
                onUpdate={this.updateTo} // eslint-disable-line @typescript-eslint/unbound-method
                minDate={this.props.minDate}
                minDateEqualTo={this.props.minDateEqualTo}
                maxDate={this.props.maxDate}
                maxDateEqualTo={this.props.maxDateEqualTo}
                prefix={this.props.prefix}
                relationship={this.props.relationship}
                forceMinDate={forceMinDate}
                onError={this.handleErrorTo} // eslint-disable-line @typescript-eslint/unbound-method
                required={this.props.required && !this.state.present && !this.props.disabled}
                hideMonth={this.props.hideToMonth}
                hideDay={this.props.hideToDay}
              />
            </div>
            <Show when={this.props.allowPresent}>
              <div className="or-present">
                <div className="from-present">
                  <span className="or"> or </span>
                </div>
                <div className="from-present">
                  <Checkbox
                    name="present"
                    className="present"
                    ariaPrepend="To Date Is "
                    label="Present"
                    value="present"
                    disabled={this.props.disabled}
                    checked={this.state.present}
                    onUpdate={this.updatePresent} // eslint-disable-line @typescript-eslint/unbound-method
                    onError={this.handleErrorPresent}
                  />
                </div>
              </div>
            </Show>
          </>
        )}
      </div>
    ) : (
      <div className={classnames('daterange', this.props.className)}>
        <div className="from-grid">
          <div className="from-label">From date</div>
          <DateControl
            attribute={setAttribute(this.props.attribute, 'from')}
            attributeList={[this.props.attribute]}
            name="from"
            className="from"
            purposeLabel="From Date"
            ref={this.from}
            {...this.state.from}
            onUpdate={this.updateFrom} // eslint-disable-line @typescript-eslint/unbound-method
            minDate={this.props.minDate}
            minDateEqualTo={this.props.minDateEqualTo}
            maxDate={this.props.maxDate}
            maxDateEqualTo={this.props.maxDateEqualTo}
            prefix={this.props.prefix}
            relationship={this.props.relationship}
            onError={this.handleErrorFrom} // eslint-disable-line @typescript-eslint/unbound-method
            disabled={this.props.disabled}
            required={this.props.required}
            hideMonth={this.props.hideFromMonth}
            hideDay={this.props.hideFromDay}
            forceMinDate={forceMinDate}
          />
        </div>
        <div className="arrow">
          <div className="arrow-right">
            <SvgComponent className="arrow-right" src="/img/arrow-right.svg" />
          </div>
        </div>
        <div className="to-grid">
          <div className="from-label">To date</div>
          <DateControl
            className={classnames('to', { 'usa-input-error': !isFFActive && this.state.error })}
            name="to"
            purposeLabel="To Date"
            subsectionInfo={this.props.subsection?.subsectionKey}
            attribute={setAttribute(this.props.attribute, 'to')}
            attributeList={[this.props.attribute]}
            ref={this.to}
            {...this.state.to}
            disabled={this.state.present || this.props.disabled}
            onUpdate={this.updateTo} // eslint-disable-line @typescript-eslint/unbound-method
            minDate={this.props.minDate}
            minDateEqualTo={this.props.minDateEqualTo}
            maxDate={this.props.maxDate}
            maxDateEqualTo={this.props.maxDateEqualTo}
            prefix={this.props.prefix}
            relationship={this.props.relationship}
            forceMinDate={forceMinDate}
            onError={this.handleErrorTo} // eslint-disable-line @typescript-eslint/unbound-method
            required={this.props.required && !this.state.present && !this.props.disabled}
            hideMonth={this.props.hideToMonth}
            hideDay={this.props.hideToDay}
          />
        </div>
        <Show when={this.props.allowPresent}>
          <div className="or-present">
            <div className="from-present">
              <span className="or"> or </span>
            </div>
            <div className="from-present">
              <Checkbox
                name="present"
                className="present"
                ariaPrepend="To Date Is "
                label="Present"
                value="present"
                disabled={this.props.disabled}
                checked={this.state.present}
                onUpdate={this.updatePresent} // eslint-disable-line @typescript-eslint/unbound-method
                onError={this.handleErrorPresent} // eslint-disable-line @typescript-eslint/unbound-method
              />
            </div>
          </div>
        </Show>
      </div>
    );
  }
}

DateRange.defaultProps = {
  from: {
    touched: false,
  },
  to: {
    touched: false,
  },
  present: false,
  prefix: '',
  dateRangePrefix: '',
  minDate: null,
  minDateEqualTo: false,
  maxDate: new Date(),
  maxDateEqualTo: false,
  relationship: '',
  allowPresent: true,
  onError: (value, arr) => arr,
  disabled: false,
  hideFromMonth: false,
  hideFromDay: false,
  hideToMonth: false,
  hideToDay: false,
  usePvqFromDate: false, // partialDateRange with unique pvq question labels
  usePvqToDate: false, // partialDateRange with unique pvq question labels
  fromDateLabel: 'From Date', // partialDateRange with unique pvq question labels
  toDateLabel: 'To Date', // partialDateRange with unique pvq question labels

};

DateRange.errors = [
  {
    code: 'required',
    func: (value, props) => {
      if (props.required && !props.disabled) {
        return validDate(value.from) && validDate(value.to);
      }
      return true;
    },
  },
  {
    code: 'daterange.order',
    func: (value, props) => {
      if (!props.from || !props.to) {
        return null;
      }
      return new DateRangeValidator(props).isValid();
    },
  },
];
export default stateFormErrorConnector(DateRange);
